import { Elements } from '@kontent-ai/delivery-sdk';
import SectionTitle, { SectionTitleProps } from '../SectionTitle/SectionTitle';
import RichTextParser from '../RichTextParser/RichTextParser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { api } from '@r-and-a-shared-ui/randa-trpc-client';
import {
  hasValue,
  getProjectType,
  ProjectTypes,
} from '@r-and-a-shared-ui/utils';
import { kontentAiDataMapping } from '@r-and-a-shared-ui/core-data-mapping';
import {
  MEMBERSHIP_MODELS,
  PLAYER_PORTALS_MODELS,
} from '@r-and-a-shared-ui/common-kontent-client';

import stylesPP from './scss/AccordionPP.module.scss';
import stylesMMB from './scss/AccordionMMB.module.scss';

export type AccordionItem = {
  title: string;
  description?: Elements.RichTextElement;
};

export interface AccordionProps {
  title?: SectionTitleProps;
  items?: AccordionItem[];
  codename: string;
  kontentItemId?: string;
}

export function Accordion({
  title,
  items,
  codename,
  kontentItemId,
}: AccordionProps) {
  const [loadedItems, setLoadedItems] = useState<
    (AccordionItem | undefined)[] | undefined
  >(items);
  const [loadedTitle, setLoadedTitle] = useState<SectionTitleProps | undefined>(
    title,
  );
  const [isDataFetched, setIsDataFetched] = useState<boolean>(true);

  const { data } = api.kontent.itemWithLinkedItems.useQuery({
    codename: isDataFetched ? '' : codename ?? '',
  });

  const projectType = getProjectType();
  const isPPproject = projectType === ProjectTypes.PlayerPortals;
  const styles = isPPproject ? stylesPP : stylesMMB;

  if (hasValue(data)) {
    const mappedCard = kontentAiDataMapping.accordion(
      data as MEMBERSHIP_MODELS.Accordion | PLAYER_PORTALS_MODELS.Accordion,
    );
    setLoadedItems(mappedCard?.items);
    setLoadedTitle(mappedCard?.title);
    setIsDataFetched(true);
  }

  useEffect(() => {
    if (items?.length && title) {
      return;
    }
    setIsDataFetched(false);
  }, [items, title, codename]);

  return (
    <div className={styles.accordion} data-kontent-item-id={kontentItemId}>
      {loadedTitle && (
        <SectionTitle
          title={loadedTitle?.title}
          backgroundImage={loadedTitle?.backgroundImage}
        />
      )}
      {loadedItems && loadedItems?.length !== 0 && (
        <div>
          {loadedItems.map((item, index) => (
            <details className={styles.accordion__section} key={index}>
              <summary className={styles.accordion__header}>
                <span className={styles.accordion__title}>{item?.title}</span>
                <div className={styles.accordion__icon}>
                  <FontAwesomeIcon
                    className={styles.accordion__button}
                    icon={faAngleDown}
                  />
                </div>
              </summary>
              <RichTextParser richText={item?.description?.value} />
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

export default Accordion;
